export interface DigitalGoodsService {
  getDetails(itemIds: string[]): Promise<any[]>;
  listPurchases(): Promise<any[]>;
  listPurchaseHistory(): Promise<any[]>;
  consume(purchaseToken: string): Promise<void>;
  acknowledge(purchaseToken: string, type: 'onetime' | 'subscription'): Promise<void>;
}

declare global {
  interface Window {
    getDigitalGoodsService?: (serviceId: string) => Promise<DigitalGoodsService>;
  }
}

export const PLAY_BILLING_SERVICE_ID = 'https://play.google.com/billing';

/**
 * Checks if the Digital Goods API is natively supported in the current environment (e.g., in a TWA).
 */
export function isNativePlayBillingSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.getDigitalGoodsService === 'function';
}

/**
 * Gets the Digital Goods Service instance for Google Play Billing.
 */
export async function getPlayBillingService(): Promise<DigitalGoodsService | null> {
  if (!isNativePlayBillingSupported()) {
    return null;
  }
  try {
    const service = await window.getDigitalGoodsService!(PLAY_BILLING_SERVICE_ID);
    return service;
  } catch (error) {
    console.error('Failed to initialize Play Digital Goods Service:', error);
    return null;
  }
}

export interface PurchaseResult {
  success: boolean;
  message: string;
  isSimulated: boolean;
  productId: 'puzzles_unlock' | 'optional_tip_jar';
  optionId: string;
  offerTag: string;
  purchaseToken?: string;
}

/**
 * Initiates the Google Play Billing flow using the PaymentRequest API and Digital Goods API.
 * Handles both one-time ('puzzles_unlock') and repeatable ('optional_tip_jar') items.
 * 
 * @param productId Either 'puzzles_unlock' or 'optional_tip_jar'
 * @param optionId The specific choice ID (e.g., 'unlock-1' through 'unlock-5' or 'support-1' through 'support-5')
 * @param offerTag The tier tag (e.g., 'tier1' through 'tier5')
 * @param price The dollar amount (1 through 5)
 */
export async function makePlayPurchase(
  productId: 'puzzles_unlock' | 'optional_tip_jar',
  optionId: string,
  offerTag: string,
  price: number
): Promise<PurchaseResult> {
  const isOneTime = productId === 'puzzles_unlock';

  // Check if we are running in a native TWA environment with Google Play Billing enabled
  if (isNativePlayBillingSupported()) {
    try {
      const service = await getPlayBillingService();
      if (!service) {
        throw new Error('Digital Goods Service could not be retrieved.');
      }

      // Configure PaymentRequest with Google Play Billing
      const paymentMethods = [
        {
          supportedMethods: PLAY_BILLING_SERVICE_ID,
          data: {
            sku: optionId,
            itemId: optionId, // redundant key for older client support
            offerToken: offerTag,
            offerTag: offerTag // redundant key
          }
        }
      ];

      const paymentDetails = {
        total: {
          label: isOneTime ? 'Bible Puzzle Quiz Sponsorship Unlock' : 'Support Tip Jar Contribution',
          amount: {
            currency: 'USD',
            value: price.toFixed(2)
          }
        }
      };

      const request = new PaymentRequest(paymentMethods, paymentDetails);
      const paymentResponse = await request.show();

      try {
        // Extract the purchase token from paymentResponse details
        const details = paymentResponse.details || {};
        const purchaseToken = details.purchaseToken || details.token || details.tokenID || `twa_token_${Date.now()}`;

        if (isOneTime) {
          // Acknowledge one-time puzzle unlock and keep it forever (do NOT consume)
          await service.acknowledge(purchaseToken, 'onetime');
        } else {
          // Consume repeatable tip jar option so it can be bought again
          await service.consume(purchaseToken);
        }

        // Complete the PaymentRequest cycle successfully
        await paymentResponse.complete('success');

        return {
          success: true,
          message: isOneTime 
            ? 'Thank you! Your permanent Bible Puzzle Quiz Sponsorship is fully unlocked.' 
            : 'Thank you for your generous tip jar contribution! Your support keeps our mission alive.',
          isSimulated: false,
          productId,
          optionId,
          offerTag,
          purchaseToken
        };
      } catch (innerError) {
        // Attempt to signal failure to the browser UI
        try {
          await paymentResponse.complete('fail');
        } catch (_) {}
        throw innerError;
      }
    } catch (error: any) {
      console.error('Play Store native purchase failed:', error);
      let rawMsg = error?.message || '';
      let message = 'The Google Play Billing transaction was cancelled or failed.';

      if (rawMsg.includes('RESULT_CANCELLED') || rawMsg.includes('AbortError') || rawMsg.includes('canceled')) {
        message = 'Payment request was cancelled by Google Play. Please check:\n\n1. In-App Products (SKUs "unlock-1" to "unlock-5" and "support-1" to "support-5") are "Active" in Play Console.\n2. The app was installed directly from the Google Play Store (or signed with the exact production certificate).\n3. Digital Asset Links (assetlinks.json) fingerprint matches your Play Store App Signing key.';
      } else if (rawMsg) {
        message = rawMsg;
      }

      return {
        success: false,
        message,
        isSimulated: false,
        productId,
        optionId,
        offerTag
      };
    }
  } else {
    // Elegant fallback simulation for standard browsers and debug sandboxes
    console.warn('getDigitalGoodsService is not supported. Running high-fidelity billing sandbox...');
    
    return new Promise((resolve) => {
      setTimeout(() => {
        const purchaseToken = `sandbox_token_${Math.random().toString(36).substring(7)}`;
        
        resolve({
          success: true,
          message: isOneTime 
            ? '[Sandbox Mode] Success! Your permanent Bible Puzzle Quiz Sponsorship is now simulated and fully active.' 
            : '[Sandbox Mode] Success! Thank you for supporting the mission with this tip jar love gift.',
          isSimulated: true,
          productId,
          optionId,
          offerTag,
          purchaseToken
        });
      }, 1500); // realistic payment latency
    });
  }
}

/**
 * Queries the Play Store Digital Goods service for existing entitlements (for restoring purchases).
 * If any 'unlock-' items are active, it triggers the unlock callback.
 */
export async function restorePurchases(onUnlockRestored: () => void): Promise<boolean> {
  if (!isNativePlayBillingSupported()) {
    return false;
  }

  try {
    const service = await getPlayBillingService();
    if (!service) return false;

    const purchases = await service.listPurchases();
    let hasRestored = false;

    if (purchases && purchases.length > 0) {
      for (const purchase of purchases) {
        // Check if item belongs to the puzzle permanent unlock catalog (starts with 'unlock-')
        if (purchase.itemId && purchase.itemId.startsWith('unlock-')) {
          console.log('Restoring existing puzzle unlock entitlement:', purchase.itemId);
          
          // Ensure it's acknowledged in case it was left in a pending state
          try {
            await service.acknowledge(purchase.purchaseToken, 'onetime');
          } catch (_) {}

          hasRestored = true;
        }
      }
    }

    if (hasRestored) {
      onUnlockRestored();
      return true;
    }
  } catch (error) {
    console.error('Failed to query or restore Play Store entitlements:', error);
  }

  return false;
}
